/**
 * safeStringify: JSON.stringify with built‑in circular‑reference protection.
 *
 * 1. Fast path: run the native JSON.stringify. Most objects are acyclic, so this
 *    finishes with the same performance as the built‑in function.
 * 2. On "Converting circular structure to JSON" errors, we retry with a replacer
 *    that marks repeated objects as "[Circular]".
 *
 * Complexity: O(n) time / space where n is the number of unique objects visited.
 * @param value - The value to stringify.
 * @param space - The number of spaces to use for indentation.
 * @returns The JSON string.
 */
export const safeStringify = (value: unknown, space: number | string = 0): string => {
  try {
    return JSON.stringify(value, null, space);
  } catch (err: any) {
    // Only handle the specific circular‑structure error; rethrow others.
    if (!(err?.name === 'TypeError' && /circular/i.test(err.message ?? ''))) throw err;
  }

  const seen = new WeakSet<object>();
  return JSON.stringify(
    value,
    (_, v) => {
      if (v && typeof v === 'object') {
        if (seen.has(v)) return '[Circular]';
        seen.add(v);
      }
      return v;
    },
    space
  );
};
